import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLazyGetDuplicateProjectsQuery,
  useLazyGetDuplicatePinnedProjectsQuery,
} from 'app/services/projectBoardApi';
import type { IDuplicateProjectRow } from 'models';
import { buildDuplicateDefaultTag } from '../../ahuUtils';
import SearchableSelect from './SearchableSelect';

interface DuplicateAhuPopupProps {
  /** Source AHU display tag (static_version_name || tag). */
  sourceTag: string;
  sourceProjectId: number;
  sourceProjectName: string;
  /** Source project editability: gates preselection + the Duplicate button. */
  editable: boolean;
  /** Confirm → POST /ahu/duplicate/unit/ {unit_id, project_id, tag|null}. */
  onConfirm: (projectId: number, tag: string | null) => void;
  onClose: () => void;
}

const PAGE_SIZE_GUARD = 100;

/**
 * Duplicate popup (`/static/mvc/view/duplicate_popup.js` +
 * duplicate_popup.tmpl, B1.6 / parity 147): heading "Create AHU Duplicate";
 * Source Project (underlined) / Source AHU / Target Project (SearchableSelect
 * in `#duclicate-project-select-wrap` — the typo id is real) / New Duplicate
 * AHU tag input (default `{src}_yyyymmdd_hhmmss`, ≤30).
 *
 * Projects are fetched lazily on open (pinned `?pinnned=true` + unpaged all)
 * behind the `.preloader.dotted-preloader.active`; server search
 * `?page=1&name={q}`; infinite scroll appends `?page={n+1}` once the list
 * reaches 100 rows. Non-editable source project: own project hidden from the
 * list, NO preselection, Duplicate starts `.non-clickable` until a target is
 * picked. Esc closes (the legacy's never-removed document listener is
 * properly cleaned up here — [NEG-quirk] not replicated).
 */
const DuplicateAhuPopup = ({
  sourceTag,
  sourceProjectId,
  sourceProjectName,
  editable,
  onConfirm,
  onClose,
}: DuplicateAhuPopupProps) => {
  const [fetchProjects] = useLazyGetDuplicateProjectsQuery();
  const [fetchPinned] = useLazyGetDuplicatePinnedProjectsQuery();

  const [loading, setLoading] = useState(false);
  const [pinnedRows, setPinnedRows] = useState<IDuplicateProjectRow[]>([]);
  const [allRows, setAllRows] = useState<IDuplicateProjectRow[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedName, setSelectedName] = useState(editable ? sourceProjectName : '');
  const [tag, setTag] = useState(() => buildDuplicateDefaultTag(sourceTag));

  const filteredRef = useRef('');
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  // Lazy first load (render → update with empty list → fetch w/ preloader).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchProjects(undefined).unwrap(), fetchPinned(undefined).unwrap()])
      .then(([all, pinned]) => {
        if (cancelled) return;
        setAllRows(all ?? []);
        setPinnedRows(pinned ?? []);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchProjects, fetchPinned]);

  // Esc closes — properly removed on unmount (legacy leaks this listener).
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.which === 27) onClose();
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [onClose]);

  // Combined pinned-first list; own project hidden when non-editable.
  const list = useMemo(() => {
    const combined = [...pinnedRows, ...allRows];
    return editable ? combined : combined.filter((row) => row.id !== sourceProjectId);
  }, [pinnedRows, allRows, editable, sourceProjectId]);

  const search = async (value: string) => {
    if (value && filteredRef.current === value) return;
    pageRef.current = 1;
    try {
      const [pinned, all] = await Promise.all([
        fetchPinned(value || undefined).unwrap(),
        value
          ? fetchProjects({ page: 1, name: value }).unwrap()
          : fetchProjects(undefined).unwrap(),
      ]);
      setPinnedRows(pinned ?? []);
      setAllRows(all ?? []);
      filteredRef.current = value;
    } catch {
      /* server filter failed — keep the current list */
    }
  };

  const scrollMore = async () => {
    if (loadingMoreRef.current) return;
    if (allRows.length < PAGE_SIZE_GUARD) return;
    const page = pageRef.current + 1;
    loadingMoreRef.current = true;
    try {
      const next = await fetchProjects({
        page,
        name: filteredRef.current,
      }).unwrap();
      setAllRows((prev) => [...prev, ...(next ?? [])]);
      pageRef.current = page;
    } catch {
      /* pagination fetch failed */
    } finally {
      loadingMoreRef.current = false;
    }
  };

  const changeProject = (id: number | null, name: string | null) => {
    setSelectedProjectId(id);
    if (name) setSelectedName(name);
    filteredRef.current = '';
    pageRef.current = 1;
  };

  const confirm = () => {
    // Duplicate starts non-clickable on non-editable projects until a target
    // is picked; the editable default target is the source project itself.
    if (!editable && selectedProjectId == null) return;
    const projectId = selectedProjectId ?? sourceProjectId;
    const trimmed = (tag || '').trim();
    onConfirm(projectId, trimmed || null);
    onClose();
  };

  const confirmLocked = !editable && selectedProjectId == null;

  return (
    <div className="popup-wrapper width-medium active">
      <div className="popup-content duplicate-popup">
        <h2 className="popup-heading">Create AHU Duplicate</h2>
        <div className="popup-content-part col-xs-12">
          <div className={`preloader dotted-preloader${loading ? ' active' : ''}`} />
          <div className="row duplicate-popup-row">
            <div className="col-xs-12">
              <div className="col-xs-3 duplicate-popup-label">
                <p className="name pt-0"> Source Project</p>
              </div>
              <div className="col-xs-9">
                <div className="row">
                  <span className="underline">{sourceProjectName}</span>
                </div>
              </div>
            </div>
          </div>
          <div className=" row duplicate-popup-header">
            <div className="col-xs-12">
              <div className="col-xs-3 duplicate-popup-label">
                <p className="name">Source AHU </p>
              </div>
              <div className="col-xs-9">
                <div className="row">
                  <span>{sourceTag}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="row duplicate-popup-row duplicate-top-margin">
            <div className="col-xs-12">
              <div className="col-xs-3 duplicate-popup-label">
                <p className="name"> Target Project</p>
              </div>
              <div className="col-xs-9">
                <div className="row mx-0" style={{ marginRight: 0 }}>
                  <div id="duclicate-project-select-wrap">
                    <SearchableSelect
                      id="duclicate-project-select"
                      list={list}
                      name={selectedName}
                      selectedId={selectedProjectId}
                      onSearch={search}
                      onScrollEnd={scrollMore}
                      onSelect={changeProject}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row duplicate-popup-row">
            <div className="col-xs-12" style={{ paddingRight: 30 }}>
              <div className="col-xs-3 duplicate-popup-label">
                <p className="name">New Duplicate AHU</p>
              </div>
              <div className="col-xs-9 mx-0" style={{ padding: 0, marginRight: 0 }}>
                <input
                  type="text"
                  className="duplicate-tag-input"
                  name="duplicate_tag"
                  value={tag}
                  maxLength={30}
                  autoComplete="off"
                  placeholder="Custom tag (defaults to <name>_yyyymmdd_hhmmss)"
                  onChange={(e) => setTag(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="popup-control-buttons" style={{ paddingTop: 20 }}>
          <button
            className={`btn right highlighted-btn${confirmLocked ? ' non-clickable' : ''}`}
            name="confirm_duplicate"
            onClick={confirm}
          >
            Duplicate
          </button>
          <button
            className="btn right btn_cancel not-highlighted-btn"
            id="btn_cancel"
            name="cancel_duplicate"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateAhuPopup;
